import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { videos } from "@/mocks/videos";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import Seo from "@/components/feature/Seo";
import VideoCard from "@/components/feature/VideoCard";
import LoadingGrid from "@/components/feature/LoadingGrid";
import EmptyState from "@/components/feature/EmptyState";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const loading = useSimulatedLoading();

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo<VideoItem[]>(() => {
    const key = query.trim().toLowerCase();
    if (!key) return videos as VideoItem[];
    return (videos as VideoItem[]).filter((video) => {
      return (
        video.title.toLowerCase().includes(key) ||
        video.summary.toLowerCase().includes(key) ||
        video.region.toLowerCase().includes(key) ||
        video.tags.some((tag) => tag.toLowerCase().includes(key))
      );
    });
  }, [query]);

  const hotVideos = useMemo(
    () => [...(videos as VideoItem[])].sort((a, b) => b.views - a.views).slice(0, 3),
    [],
  );

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <Seo
        title={query ? `“${query}”的搜索结果 - 黄瓜视频下载` : "全部视频 - 高清视频资源导航 - 黄瓜视频下载"}
        description={
          query
            ? `黄瓜视频下载中与“${query}”相关的视频资源搜索结果，按标题、标签与简介进行匹配，共找到 ${results.length} 个演示视频条目。`
            : "浏览黄瓜视频下载全部高清视频资源条目，涵盖电影、电视剧、综艺、动漫、纪录片等多样分类。"
        }
        keywords={`黄瓜视频下载,视频搜索,${query || "全部视频"},高清视频,视频资源`}
      />

      <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6 md:pt-8">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {query ? "搜索结果" : "全部视频资源"}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            {query ? (
              <>
                当前搜索词：<span className="font-medium text-primary-600">“{query}”</span>
              </>
            ) : (
              "输入关键词即可按标题、标签与简介进行模糊匹配"
            )}
          </p>
          <form
            onSubmit={submitSearch}
            className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="text"
                name="q"
                placeholder="输入视频标题、标签或地区"
                className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>
          <p className="mt-4 text-xs text-foreground-500">
            {query ? `共找到 ${results.length} 个相关视频条目` : `共 ${results.length} 个视频条目`}
          </p>
        </div>

        <div className="mt-8">
          {loading ? (
            <LoadingGrid count={8} />
          ) : results.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
              {results.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              <EmptyState
                icon="ri-search-eye-line"
                title="未找到匹配视频"
                description="未找到与当前关键词匹配的视频，尝试修改关键词，或返回首页浏览热门分类。"
                actionLabel="返回首页"
                actionTo="/"
              />
              <div>
                <div className="mb-6 flex items-center gap-3">
                  <span className="h-6 w-1 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">热门视频推荐</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
                  {hotVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
                <div className="mt-6 text-center">
                  <Link
                    to="/search"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-apps-2-line"></i>
                    浏览全部视频
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}